import { API_BASE, ApiError, type HttpTransport } from './http';
import { ApiErrorCodes } from './errors';
import type { MailApi } from './contract';
import type {
  ScheduledCancelResult,
  ScheduledSend,
  ScheduledSendList,
  ScheduledSendNowResult,
  SchedulingStatus,
  SnoozeList,
  SnoozeResult,
  UnsnoozeResult,
  PartLink,
  RenderStats,
  RenderTicket,
  AttachedMessage,
  Account,
  AiAssistResult,
  AiMessageAssistMeta,
  AiKeyTestResult,
  AiModels,
  AiSettings,
  AddressBook,
  AddressBookInput,
  AddressBookPatch,
  BackgroundAccountStatus,
  BackgroundPushStatus,
  BlockedSenderList,
  BlockSenderResult,
  BulkResult,
  TransferResult,
  Calendar,
  CalendarEvent,
  CalendarInput,
  CalendarPatch,
  CalendarShareLinkList,
  CalendarSubscriptionInput,
  ChangePasswordResult,
  ComposePrefill,
  Contact,
  ContactPage,
  ContactImportReport,
  ConversationDetail,
  ConversationPage,
  ConversationSyncResult,
  Draft,
  Folder,
  FolderRoleChoices,
  EmptyFolderResult,
  Label,
  LabelMigrateResult,
  MessageAuthentication,
  NextcloudLinkStarted,
  NextcloudListing,
  NextcloudFolderCreated,
  NextcloudPollPending,
  NextcloudSaveEvent,
  NextcloudShared,
  NextcloudStatus,
  NextcloudUnlinked,
  NextcloudUpload,
  NextcloudUploadPage,
  NextcloudUploadReserved,
  MessageDetail,
  MessageSummary,
  MintedCalendarShareLink,
  NewMailPoll,
  NotificationPreferences,
  OwnerSnooze,
  TestPushResult,
  NotificationPreview,
  NotificationRule,
  Paginated,
  PushPublicKey,
  PushSubscriptionRecord,
  Quota,
  RebindRequest,
  RemoteContentRule,
  RevokeOthersRequest,
  RevokeOthersResult,
  SearchPage,
  SavedSentCopy,
  SendResult,
  SentCopy,
  SentCopyRunResult,
  SentSettings,
  StagedAttachment,
  Session,
  SessionSummary,
  SieveBackup,
  SieveBackupList,
  SieveState,
  SieveTestResult,
  VacationState,
  StreamTicket,
  UnsubscribeResult,
  UserPreferences,
  Task,
  TaskEmailResolution,
  TaskList,
  TaskListsResponse,
  VideoCallTestResult,
  ConnectionProbe,
  ProvidersResponse,
  OAuthStartResponse,
} from './types';

/**
 * Real backend implementation of {@link MailApi}.
 *
 * Every method is a one-liner against the transport on purpose: the paths here
 * *implement* `docs/plan/10-api-contract.md` §5, which is the contract. When
 * the two disagree, the document wins and this file is the bug.
 *
 * Two things this file deliberately does not do:
 *
 * - **Case conversion.** Bodies and query objects are written in camelCase and
 *   `HttpTransport` converts them to `snake_case` on the way out (ADR 0009).
 *   A `snake_case` key should never appear below this comment.
 * - **Id construction.** `folderId` and `messageId` are opaque base64url
 *   strings; they are concatenated into paths and never parsed, split or
 *   derived from one another. `encodeURIComponent` on a base64url or UUID
 *   value is a no-op, and it is applied anyway so the rule never has to be
 *   re-established the day an id encoding changes.
 */
export function createHttpApi(http: HttpTransport): MailApi {
  /** `/accounts/{id}` — the prefix of every mailbox-owned resource. */
  const acct = (accountId: string): string => `/accounts/${encodeURIComponent(accountId)}`;
  const id = encodeURIComponent;

  /** One streamed assistant answer (ADR 0065): `meta` (message assists only), `delta`s, then `done`. */
  const streamAiAnswer = async (
    path: string,
    body: unknown,
    onText: (text: string) => void,
    onMeta: ((meta: AiMessageAssistMeta) => void) | undefined,
    signal: AbortSignal | undefined,
  ): Promise<AiAssistResult> => {
    let stopReason = '';
    let about: Omit<AiAssistResult, 'stopReason'> = {};
    await http.requestEventStream(
      path,
      body,
      (event, data) => {
        const payload = (data ?? {}) as {
          text?: unknown;
          stopReason?: unknown;
          model?: unknown;
          modelName?: unknown;
          modelNotice?: unknown;
        };
        if (event === 'delta' && typeof payload.text === 'string') onText(payload.text);
        else if (event === 'meta') onMeta?.(data as AiMessageAssistMeta);
        else if (event === 'done') {
          stopReason = typeof payload.stopReason === 'string' ? payload.stopReason : 'end_turn';
          // Which model answered: optional, and only strings that say something.
          const text = (v: unknown): string | undefined => (typeof v === 'string' && v !== '' ? v : undefined);
          const model = text(payload.model);
          const modelName = text(payload.modelName);
          const modelNotice = text(payload.modelNotice);
          about = {
            ...(model !== undefined ? { model } : {}),
            ...(modelName !== undefined ? { modelName } : {}),
            ...(modelNotice !== undefined ? { modelNotice } : {}),
          };
        }
      },
      signal,
    );
    // A stream that ended with neither `done` nor `error` was cut off on
    // the way (a proxy, a dropped connection): half an answer is not one.
    if (stopReason === '') {
      throw new ApiError(502, {
        error: {
          code: ApiErrorCodes.AIUpstreamError,
          message: 'The answer was cut off before it finished. Try again.',
        },
      });
    }
    return { stopReason, ...about };
  };

  return {
    auth: {
      login: (input, signal) =>
        http.request<Session>('/auth/login', {
          method: 'POST',
          body: input,
          ...(signal ? { signal } : {}),
        }),
      logout: () => http.request<void>('/auth/logout', { method: 'POST' }),
      session: (signal) => http.request<Session>('/auth/session', signal ? { signal } : {}),
      refresh: () => http.request<Session>('/auth/refresh', { method: 'POST' }),
      changePassword: (accountId, input) =>
        http.request<ChangePasswordResult>(`${acct(accountId)}/password`, {
          method: 'POST',
          body: input,
        }),
      rebind: (input: RebindRequest) =>
        http.request<Session>('/auth/rebind', { method: 'POST', body: input }),
      sessions: (signal) =>
        http.request<SessionSummary[]>('/auth/sessions', signal ? { signal } : {}),
      revokeSession: (sessionId) =>
        http.request<void>(`/auth/sessions/${id(sessionId)}`, { method: 'DELETE' }),
      revokeOtherSessions: (password, keepEndpoint) => {
        // `keep_endpoint` on the wire, spelled exactly (the backend decodes with
        // DisallowUnknownFields). Left out when this browser has no push
        // subscription, rather than sent empty.
        const body: RevokeOthersRequest =
          keepEndpoint === undefined ? { password } : { password, keepEndpoint };
        return http.request<RevokeOthersResult>('/auth/sessions/revoke-others', {
          method: 'POST',
          body,
        });
      },
    },

    events: {
      // Single-use and short-lived: minted immediately before an open and
      // never kept. `connection.ts` mints a *new* one for every reopen.
      ticket: (signal) =>
        http.request<StreamTicket>('/events/ticket', {
          method: 'POST',
          ...(signal ? { signal } : {}),
        }),
    },

    accounts: {
      list: (signal) => http.request<Account[]>('/accounts', signal ? { signal } : {}),
      get: (accountId, signal) => http.request<Account>(acct(accountId), signal ? { signal } : {}),
      providers: async (signal) => {
        try {
          return await http.request<ProvidersResponse>('/accounts/providers', signal ? { signal } : {});
        } catch (cause) {
          // An older backend has no catalogue: it reads `providers` as an
          // account id (404) or answers that the route is not built (501).
          // Either way there are no external providers, not a broken form.
          if (cause instanceof ApiError && [404, 405, 501].includes(cause.status)) {
            return { enabled: false, providers: [] };
          }
          throw cause;
        }
      },
      create: (input) => http.request<Account>('/accounts', { method: 'POST', body: input }),
      update: (accountId, input) =>
        http.request<Account>(acct(accountId), { method: 'PATCH', body: input }),
      remove: (accountId) => http.request<void>(acct(accountId), { method: 'DELETE' }),
      verify: (accountId, password) =>
        http.request<Account>(`${acct(accountId)}/verify`, {
          method: 'POST',
          // `{}` rather than `{ password: undefined }`: no password means
          // "re-check the stored one", and the key must be absent to say so.
          body: password === undefined ? {} : { password },
        }),
      // A probe is a POST like any other, so the transport gives each click
      // its own Idempotency-Key: "Test each service" pressed twice runs twice,
      // and a key can never be replayed with a different password.
      probe: (accountId, password) =>
        http.request<ConnectionProbe>(`${acct(accountId)}/probe`, {
          method: 'POST',
          // As `verify`: no key means "the stored password".
          body: password === undefined ? {} : { password },
        }),
      probeNew: (input) =>
        http.request<ConnectionProbe>('/accounts/probe', { method: 'POST', body: input }),
      oauthStart: (input) =>
        http.request<OAuthStartResponse>('/accounts/oauth/start', { method: 'POST', body: input }),
      quota: (accountId, signal) =>
        http.request<Quota>(`${acct(accountId)}/quota`, signal ? { signal } : {}),
    },

    folders: {
      list: (accountId, signal) =>
        http.request<Folder[]>(`${acct(accountId)}/folders`, signal ? { signal } : {}),
      create: (accountId, input) =>
        http.request<Folder>(`${acct(accountId)}/folders`, { method: 'POST', body: input }),
      rename: (accountId, folderId, name) =>
        http.request<Folder>(`${acct(accountId)}/folders/${id(folderId)}`, {
          method: 'PATCH',
          body: { name },
        }),
      remove: (accountId, folderId, recursive) =>
        http.request<void>(`${acct(accountId)}/folders/${id(folderId)}`, {
          method: 'DELETE',
          ...(recursive === true ? { query: { recursive: 1 } } : {}),
        }),
      markAllRead: (accountId, folderId) =>
        http.request<{ updatedCount: number }>(
          `${acct(accountId)}/folders/${id(folderId)}/mark-read`,
          { method: 'POST' },
        ),
      focus: (accountId, folderId) =>
        http.request<void>(`${acct(accountId)}/folders/${id(folderId)}/focus`, { method: 'PUT' }),
      empty: (accountId, folderId, confirmation) =>
        http.request<EmptyFolderResult>(`${acct(accountId)}/folders/${id(folderId)}/empty`, {
          method: 'POST',
          ...(confirmation === undefined ? {} : { body: confirmation }),
        }),
      roles: (accountId, signal) =>
        http.request<FolderRoleChoices>(`${acct(accountId)}/folder-roles`, signal ? { signal } : {}),
      setRole: (accountId, role, folderId) =>
        http.request<FolderRoleChoices>(`${acct(accountId)}/folder-roles/${id(role)}`, {
          method: 'PUT',
          body: { folderId },
        }),
      clearRole: (accountId, role) =>
        http.request<void>(`${acct(accountId)}/folder-roles/${id(role)}`, { method: 'DELETE' }),
    },

    messages: {
      // The folder is a path segment, not a filter: a message list is a view of
      // one mailbox, and `409 folder_invalidated` is scoped to it.
      list: ({ accountId, folderId, ...query }, signal) =>
        http.request<Paginated<MessageSummary>>(
          `${acct(accountId)}/folders/${id(folderId)}/messages`,
          { query: { ...query }, ...(signal ? { signal } : {}) },
        ),
      // `?remote=` is passed through only when the caller asked for a mode; with
      // no parameter the server applies the persisted rule, which is the whole
      // point of persisting one.
      get: (accountId, messageId, options) =>
        http.request<MessageDetail>(`${acct(accountId)}/messages/${id(messageId)}`, {
          ...(options?.remote ? { query: { remote: options.remote } } : {}),
          ...(options?.signal ? { signal: options.signal } : {}),
        }),
      raw: (accountId, messageId, signal) =>
        http.requestText(
          `${acct(accountId)}/messages/${id(messageId)}/source`,
          signal ? { signal } : {},
        ),
      rawBlob: (accountId, messageId, signal) =>
        http.requestBlob(
          `${acct(accountId)}/messages/${id(messageId)}/source`,
          signal ? { signal } : {},
        ),
      // The user-content origin (ADR 0078). Always a JSON body: the server
      // decodes with DisallowUnknownFields and requires one.
      render: (accountId, messageId, options) =>
        http.request<RenderTicket>(`${acct(accountId)}/messages/${id(messageId)}/render`, {
          method: 'POST',
          body: {
            // The reader's choice, when they made one; absent, their saved
            // rules decide, as for the message itself.
            ...(options?.remote !== undefined ? { remote: options.remote } : {}),
            ...(options?.appearance === 'dark' ? { appearance: 'dark' } : {}),
          },
          ...(options?.signal ? { signal: options.signal } : {}),
        }),
      renderStats: (accountId, messageId, renderId, signal) =>
        http.request<RenderStats>(
          `${acct(accountId)}/messages/${id(messageId)}/renders/${id(renderId)}/stats`,
          signal ? { signal } : {},
        ),
      partLink: (accountId, messageId, partId, disposition) =>
        http.request<PartLink>(
          `${acct(accountId)}/messages/${id(messageId)}/parts/${id(partId)}/link`,
          { method: 'POST', body: { disposition } },
        ),
      // An attached email (ADR 0092). The path is part ids joined by '-',
      // which encodeURIComponent leaves as it is.
      attached: (accountId, messageId, path, options) =>
        http.request<AttachedMessage>(
          `${acct(accountId)}/messages/${id(messageId)}/attached/${id(path)}`,
          {
            ...(options?.remote ? { query: { remote: options.remote } } : {}),
            ...(options?.signal ? { signal: options.signal } : {}),
          },
        ),
      renderAttached: (accountId, messageId, path, options) =>
        http.request<RenderTicket>(
          `${acct(accountId)}/messages/${id(messageId)}/attached/${id(path)}/render`,
          {
            method: 'POST',
            body: {
              ...(options?.remote !== undefined ? { remote: options.remote } : {}),
              ...(options?.appearance === 'dark' ? { appearance: 'dark' } : {}),
            },
            ...(options?.signal ? { signal: options.signal } : {}),
          },
        ),
      renderAttachedStats: (accountId, messageId, path, renderId, signal) =>
        http.request<RenderStats>(
          `${acct(accountId)}/messages/${id(messageId)}/attached/${id(path)}/renders/${id(renderId)}/stats`,
          signal ? { signal } : {},
        ),
      attachedPartLink: (accountId, messageId, path, partId, disposition) =>
        http.request<PartLink>(
          `${acct(accountId)}/messages/${id(messageId)}/attached/${id(path)}/parts/${id(partId)}/link`,
          { method: 'POST', body: { disposition } },
        ),
      authentication: (accountId, messageId, signal) =>
        http.request<MessageAuthentication>(
          `${acct(accountId)}/messages/${id(messageId)}/authentication`,
          signal ? { signal } : {},
        ),
      updateFlags: (accountId, messageIds, update) =>
        http.request<BulkResult>(`${acct(accountId)}/messages/flags`, {
          method: 'PATCH',
          body: { messageIds, ...update },
        }),
      move: (accountId, input) =>
        http.request<BulkResult>(`${acct(accountId)}/messages/move`, {
          method: 'POST',
          body: input,
        }),
      copy: (accountId, input) =>
        http.request<BulkResult>(`${acct(accountId)}/messages/copy`, {
          method: 'POST',
          body: input,
        }),
      transfer: (accountId, input) =>
        http.request<TransferResult>(`${acct(accountId)}/messages/transfer`, {
          method: 'POST',
          body: input,
        }),
      archive: (accountId, messageIds) =>
        http.request<BulkResult>(`${acct(accountId)}/messages/archive`, {
          method: 'POST',
          body: { messageIds },
        }),
      remove: (accountId, messageIds, expunge) =>
        http.request<BulkResult>(`${acct(accountId)}/messages/delete`, {
          method: 'POST',
          body: { messageIds, ...(expunge === undefined ? {} : { expunge }) },
        }),
      reportSpam: (accountId, messageIds) =>
        http.request<BulkResult>(`${acct(accountId)}/messages/spam`, {
          method: 'POST',
          body: { messageIds },
        }),
      notSpam: (accountId, messageIds) =>
        http.request<BulkResult>(`${acct(accountId)}/messages/not-spam`, {
          method: 'POST',
          body: { messageIds },
        }),
      // No body at all: the server reads the header off the message itself,
      // so there is nothing the client could say that it should trust.
      unsubscribe: (accountId, messageId) =>
        http.request<UnsubscribeResult>(
          `${acct(accountId)}/messages/${id(messageId)}/unsubscribe`,
          { method: 'POST' },
        ),
      // `scope` and `includeTrashJunk` go on the wire only when set: an absent
      // scope is the one-folder search this endpoint has always answered.
      search: (accountId, { query, includeTrashJunk, ...page }, signal) =>
        http.request<SearchPage>(`${acct(accountId)}/search`, {
          query: { q: query, ...page, ...(includeTrashJunk ? { includeTrashJunk: true } : {}) },
          ...(signal ? { signal } : {}),
        }),
      // Conversation view (ADR 0077). The same folder path as `list`; a
      // search is not a conversation list and is never sent here.
      conversations: ({ accountId, folderId, ...query }, signal) =>
        http.request<ConversationPage>(
          `${acct(accountId)}/folders/${id(folderId)}/conversations`,
          { query: { ...query }, ...(signal ? { signal } : {}) },
        ),
      // A POST only because the held rows can be long; it changes nothing.
      syncConversations: (accountId, folderId, input, signal) =>
        http.request<ConversationSyncResult>(
          `${acct(accountId)}/folders/${id(folderId)}/conversations/sync`,
          { method: 'POST', body: input, ...(signal ? { signal } : {}) },
        ),
      conversation: (accountId, messageId, signal) =>
        http.request<ConversationDetail>(
          `${acct(accountId)}/messages/${id(messageId)}/conversation`,
          signal ? { signal } : {},
        ),
    },

    compose: {
      listDrafts: (accountId, signal) =>
        http.request<Draft[]>(`${acct(accountId)}/drafts`, signal ? { signal } : {}),
      getDraft: (accountId, draftId, signal) =>
        http.request<Draft>(`${acct(accountId)}/drafts/${id(draftId)}`, signal ? { signal } : {}),
      createDraft: (accountId, input) =>
        http.request<Draft>(`${acct(accountId)}/drafts`, { method: 'POST', body: input }),
      saveDraft: (accountId, draftId, input) =>
        http.request<Draft>(`${acct(accountId)}/drafts/${id(draftId)}`, {
          method: 'PUT',
          body: input,
        }),
      discardDraft: (accountId, draftId) =>
        http.request<void>(`${acct(accountId)}/drafts/${id(draftId)}`, { method: 'DELETE' }),
      send: (accountId, draftId) =>
        http.request<SendResult>(`${acct(accountId)}/drafts/${id(draftId)}/send`, {
          method: 'POST',
        }),
      sendDirect: (accountId, input, options) =>
        http.request<SendResult>(`${acct(accountId)}/send`, {
          method: 'POST',
          body: input,
          ...(options?.idempotencyKey !== undefined
            ? { idempotencyKey: options.idempotencyKey }
            : {}),
        }),
      prepareReply: (accountId, messageId, mode, signal) =>
        http.request<ComposePrefill>(`${acct(accountId)}/compose/reply`, {
          query: { messageId, mode },
          ...(signal ? { signal } : {}),
        }),
      uploadAttachment: (accountId, file, options) => {
        // Multipart field names are spelled on the wire directly: `FormData` is
        // passed through the transport by reference, never case-converted.
        const form = new FormData();
        form.append('file', file);
        // `requestUpload`, not `request`: the same headers and the same
        // recoveries, over XHR so the composer can show a progress bar.
        return http.requestUpload<StagedAttachment>(`${acct(accountId)}/attachments`, {
          method: 'POST',
          body: form,
          ...(options?.onProgress ? { onProgress: options.onProgress } : {}),
          ...(options?.signal ? { signal: options.signal } : {}),
        });
      },
      discardAttachment: (accountId, attachmentId) =>
        http.request<void>(`${acct(accountId)}/attachments/${id(attachmentId)}`, {
          method: 'DELETE',
        }),
    },

    sieve: {
      get: (accountId, signal) =>
        http.request<SieveState>(`${acct(accountId)}/sieve`, signal ? { signal } : {}),
      put: (accountId, input) =>
        http.request<SieveState>(`${acct(accountId)}/sieve`, { method: 'PUT', body: input }),
      test: (accountId, input) =>
        http.request<SieveTestResult>(`${acct(accountId)}/sieve/test`, {
          method: 'POST',
          body: input,
        }),
      getVacation: (accountId, signal) =>
        http.request<VacationState>(`${acct(accountId)}/sieve/vacation`, signal ? { signal } : {}),
      putVacation: (accountId, input) =>
        http.request<VacationState>(`${acct(accountId)}/sieve/vacation`, {
          method: 'PUT',
          body: input,
        }),
      listBackups: (accountId, signal) =>
        http.request<SieveBackupList>(
          `${acct(accountId)}/sieve/backups`,
          signal ? { signal } : {},
        ),
      getBackup: (accountId, backupId, signal) =>
        http.request<SieveBackup>(
          `${acct(accountId)}/sieve/backups/${id(String(backupId))}`,
          signal ? { signal } : {},
        ),
      restoreBackup: (accountId, backupId, input) =>
        http.request<SieveState>(
          `${acct(accountId)}/sieve/backups/${id(String(backupId))}/restore`,
          // Spelled out: the handler decodes with DisallowUnknownFields.
          { method: 'POST', body: { baseHash: input.baseHash } },
        ),
    },

    blockedSenders: {
      list: (accountId, signal) =>
        http.request<BlockedSenderList>(
          `${acct(accountId)}/blocked-senders`,
          signal ? { signal } : {},
        ),
      block: (accountId, input) =>
        http.request<BlockSenderResult>(`${acct(accountId)}/blocked-senders`, {
          method: 'POST',
          // Spelled out rather than passed through: the handler decodes with
          // DisallowUnknownFields, and `folderId` must be absent, not
          // `undefined`, when there is no folder to name.
          body: {
            address: input.address,
            moveExisting: input.moveExisting,
            ...(input.folderId !== undefined ? { folderId: input.folderId } : {}),
          },
        }),
      unblock: (accountId, address) =>
        http.request<void>(`${acct(accountId)}/blocked-senders`, {
          method: 'DELETE',
          query: { address },
        }),
    },

    contacts: {
      listBooks: (accountId, signal) =>
        http.request<AddressBook[]>(`${acct(accountId)}/address-books`, signal ? { signal } : {}),
      createBook: (accountId, input: AddressBookInput) =>
        http.request<AddressBook>(`${acct(accountId)}/address-books`, {
          method: 'POST',
          body: input,
        }),
      updateBook: (accountId, bookId, input: AddressBookPatch) =>
        http.request<AddressBook>(`${acct(accountId)}/address-books/${id(bookId)}`, {
          method: 'PATCH',
          // The object is already sparse -- the caller omits what it is not
          // changing -- so it is passed straight through. Spreading a whole
          // AddressBook in here would silently rewrite every property.
          body: input,
        }),
      removeBook: (accountId, bookId, confirm) =>
        http.request<void>(`${acct(accountId)}/address-books/${id(bookId)}`, {
          method: 'DELETE',
          ...(confirm === undefined ? {} : { query: { confirm } }),
        }),
      list: (accountId, { addressBookId, ...query }, signal) =>
        http.request<ContactPage>(
          `${acct(accountId)}/address-books/${id(addressBookId)}/contacts`,
          { query: { ...query }, ...(signal ? { signal } : {}) },
        ),
      get: (accountId, contactId, signal) =>
        http.request<Contact>(
          `${acct(accountId)}/contacts/${id(contactId)}`,
          signal ? { signal } : {},
        ),
      create: (accountId, addressBookId, input) =>
        http.request<Contact>(`${acct(accountId)}/address-books/${id(addressBookId)}/contacts`, {
          method: 'POST',
          body: input,
        }),
      update: (accountId, contactId, input, etag) =>
        http.request<Contact>(`${acct(accountId)}/contacts/${id(contactId)}`, {
          method: 'PUT',
          body: input,
          ...(etag !== undefined ? { headers: { 'If-Match': etag } } : {}),
        }),
      remove: (accountId, contactId, etag) =>
        http.request<void>(`${acct(accountId)}/contacts/${id(contactId)}`, {
          method: 'DELETE',
          ...(etag !== undefined ? { headers: { 'If-Match': etag } } : {}),
        }),
      photo: (accountId, contactId, signal) =>
        http.requestBlob(
          `${acct(accountId)}/contacts/${id(contactId)}/photo`,
          signal ? { signal } : {},
        ),
      exportVcf: (accountId, addressBookId, contactId) =>
        http.requestBlob(`${acct(accountId)}/address-books/${id(addressBookId)}/export.vcf`, {
          ...(contactId !== undefined ? { query: { contactId } } : {}),
        }),
      importVcf: (accountId, addressBookId, file, options) => {
        const form = new FormData();
        form.append('file', file);
        // Over XHR like uploadAttachment, for the progress bar. The import is
        // idempotent by construction -- a second run skips every card the
        // first one wrote -- so the transport's single retry is safe.
        return http.requestUpload<ContactImportReport>(
          `${acct(accountId)}/address-books/${id(addressBookId)}/import`,
          {
            method: 'POST',
            body: form,
            ...(options?.onProgress ? { onProgress: options.onProgress } : {}),
            ...(options?.signal ? { signal: options.signal } : {}),
          },
        );
      },
      autocomplete: (accountId, query, signal) =>
        http.request<Contact[]>(`${acct(accountId)}/contacts`, {
          query: { q: query },
          ...(signal ? { signal } : {}),
        }),
    },

    calendar: {
      listCalendars: (accountId, signal) =>
        http.request<Calendar[]>(`${acct(accountId)}/calendars`, signal ? { signal } : {}),
      createCalendar: (accountId, input: CalendarInput) =>
        http.request<Calendar>(`${acct(accountId)}/calendars`, { method: 'POST', body: input }),
      updateCalendar: (accountId, calendarId, input: CalendarPatch) =>
        http.request<Calendar>(`${acct(accountId)}/calendars/${id(calendarId)}`, {
          method: 'PATCH',
          body: input,
        }),
      removeCalendar: (accountId, calendarId, confirm) =>
        http.request<void>(`${acct(accountId)}/calendars/${id(calendarId)}`, {
          method: 'DELETE',
          // Absent rather than `confirm=undefined`: the *absence* of the
          // parameter is what asks the server to count instead of delete.
          ...(confirm === undefined ? {} : { query: { confirm } }),
        }),
      subscribeCalendar: (accountId, input: CalendarSubscriptionInput) =>
        http.request<Calendar>(`${acct(accountId)}/calendar-subscriptions`, {
          method: 'POST',
          body: input,
        }),
      // `calendarId` (singular) with an array value: the transport appends one
      // `calendar_id=` parameter per entry, which is the wire shape in §5.8.
      listEvents: ({ accountId, calendarIds, ...range }, signal) =>
        http.request<CalendarEvent[]>(`${acct(accountId)}/calendar-events`, {
          query: { ...range, ...(calendarIds ? { calendarId: calendarIds } : {}) },
          ...(signal ? { signal } : {}),
        }),
      getEvent: (accountId, eventId, signal) =>
        http.request<CalendarEvent>(
          `${acct(accountId)}/calendar-events/${id(eventId)}`,
          signal ? { signal } : {},
        ),
      createEvent: (accountId, calendarId, input) =>
        http.request<CalendarEvent>(`${acct(accountId)}/calendars/${id(calendarId)}/events`, {
          method: 'POST',
          body: input,
        }),
      updateEvent: (accountId, eventId, input, etag) =>
        http.request<CalendarEvent>(`${acct(accountId)}/calendar-events/${id(eventId)}`, {
          method: 'PUT',
          body: input,
          ...(etag !== undefined ? { headers: { 'If-Match': etag } } : {}),
        }),
      removeEvent: (accountId, eventId, scope) =>
        http.request<void>(`${acct(accountId)}/calendar-events/${id(eventId)}`, {
          method: 'DELETE',
          ...(scope ? { query: { ...scope } } : {}),
        }),
      respond: (accountId, eventId, status) =>
        http.request<void>(`${acct(accountId)}/calendar-events/${id(eventId)}/respond`, {
          method: 'POST',
          body: { status },
        }),
      replyToInvite: (accountId, calendarId, input) =>
        http.request<CalendarEvent>(
          `${acct(accountId)}/calendars/${id(calendarId)}/invite-reply`,
          {
            method: 'POST',
            // `message_id` / `part_id` on the wire — `toWire` does the casing.
            // The ICS itself stays in the mailbox: the server re-reads it.
            body: {
              messageId: input.messageId,
              partId: input.partId,
              status: input.status,
              ...(input.confirmUnverified === true ? { confirmUnverified: true } : {}),
            },
          },
        ),
      exportIcs: (accountId, calendarId, eventId) =>
        http.requestBlob(`${acct(accountId)}/calendars/${id(calendarId)}/export.ics`, {
          query: eventId === undefined ? {} : { eventId },
        }),
      listShareLinks: (accountId, calendarId, signal) =>
        http.request<CalendarShareLinkList>(
          `${acct(accountId)}/calendars/${id(calendarId)}/share-links`,
          signal ? { signal } : {},
        ),
      // The resolved value carries the one and only copy of the URL. It is
      // returned to the caller and deliberately not cached anywhere here: a
      // bearer credential for a whole calendar has no business in a query
      // cache that this app persists to disk.
      createShareLink: (accountId, calendarId, input) =>
        http.request<MintedCalendarShareLink>(
          `${acct(accountId)}/calendars/${id(calendarId)}/share-links`,
          { method: 'POST', body: input },
        ),
      revokeShareLink: (accountId, calendarId, linkId) =>
        http.request<void>(
          `${acct(accountId)}/calendars/${id(calendarId)}/share-links/${id(linkId)}`,
          { method: 'DELETE' },
        ),
    },

    push: {
      publicKey: (signal) =>
        http.request<PushPublicKey>('/push/public-key', signal ? { signal } : {}),
      subscribe: (input) =>
        http.request<PushSubscriptionRecord>('/push/subscriptions', { method: 'POST', body: input }),
      unsubscribe: (subscriptionId) =>
        http.request<void>(`/push/subscriptions/${id(subscriptionId)}`, { method: 'DELETE' }),
      listSubscriptions: (signal) =>
        http.request<PushSubscriptionRecord[]>('/push/subscriptions', signal ? { signal } : {}),
      getBackgroundPush: (signal) =>
        http.request<BackgroundPushStatus>('/push/background', signal ? { signal } : {}),
      enableBackgroundPush: (input) =>
        http.request<BackgroundPushStatus>('/push/background', {
          method: 'POST',
          body: { password: input.password, consentVersion: input.consentVersion },
        }),
      disableBackgroundPush: () => http.request<void>('/push/background', { method: 'DELETE' }),
      listBackgroundAccounts: (signal) =>
        http.request<BackgroundAccountStatus[]>(
          '/push/background/accounts',
          signal ? { signal } : {},
        ),
      enableBackgroundAccount: (accountId, input) =>
        http.request<BackgroundAccountStatus>(`/push/background/accounts/${id(accountId)}`, {
          method: 'POST',
          body: { password: input.password, consentVersion: input.consentVersion },
        }),
      disableBackgroundAccount: (accountId) =>
        http.request<void>(`/push/background/accounts/${id(accountId)}`, { method: 'DELETE' }),
      getPreferences: (accountId, signal) =>
        http.request<NotificationPreferences>(
          `${acct(accountId)}/notification-preferences`,
          signal ? { signal } : {},
        ),
      setPreferences: (accountId, prefs) =>
        http.request<NotificationPreferences>(`${acct(accountId)}/notification-preferences`, {
          method: 'PUT',
          body: prefs,
        }),
      setOwnerSnooze: (until) =>
        http.request<OwnerSnooze>('/notifications/snooze', {
          method: 'PUT',
          body: { until },
        }),
      // No body: the server picks this sign-in's subscriptions itself. A fresh
      // Idempotency-Key per press (the transport's default), so a second press
      // is a second test, not a replay of the first.
      sendTestPush: () => http.request<TestPushResult>('/push/test', { method: 'POST' }),
    },

    remoteContent: {
      // The server wrote this path into the sanitized body; we only fetch it.
      // The origin check is the guard that keeps this from becoming an open
      // proxy the page can be talked into calling.
      subresource: (path, signal) => {
        const base = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
        const rel = path.startsWith(`${base}/`) ? path.slice(base.length) : path;
        if (!rel.startsWith('/')) {
          return Promise.reject(
            new Error('subresource: refused a path that is not on this API'),
          );
        }
        return http.requestBlob(rel, signal ? { signal } : {});
      },
      listRules: (accountId, signal) =>
        http.request<RemoteContentRule[]>(
          `${acct(accountId)}/remote-content-rules`,
          signal ? { signal } : {},
        ),
      addRule: (accountId, input) =>
        http.request<RemoteContentRule>(`${acct(accountId)}/remote-content-rules`, {
          method: 'POST',
          body: input,
        }),
      removeRule: (accountId, ruleId) =>
        http.request<void>(`${acct(accountId)}/remote-content-rules/${id(ruleId)}`, {
          method: 'DELETE',
        }),
      clearRules: (accountId) =>
        http.request<void>(`${acct(accountId)}/remote-content-rules`, { method: 'DELETE' }),
      allowSvgForMessage: (accountId, messageId) =>
        http.request<void>(`${acct(accountId)}/messages/${id(messageId)}/remote-svg`, {
          method: 'POST',
        }),
      allowSvgForAttached: (accountId, messageId, path) =>
        http.request<void>(
          `${acct(accountId)}/messages/${id(messageId)}/attached/${id(path)}/remote-svg`,
          { method: 'POST' },
        ),
    },

    notifications: {
      listRules: (accountId, signal) =>
        http.request<NotificationRule[]>(
          `${acct(accountId)}/notification-rules`,
          signal ? { signal } : {},
        ),
      addRule: (accountId, input) =>
        http.request<NotificationRule>(`${acct(accountId)}/notification-rules`, {
          method: 'POST',
          body: input,
        }),
      removeRule: (accountId, ruleId) =>
        http.request<void>(`${acct(accountId)}/notification-rules/${id(ruleId)}`, {
          method: 'DELETE',
        }),
      preview: (accountId, limit, signal) =>
        http.request<NotificationPreview>(`${acct(accountId)}/notification-rules/preview`, {
          ...(limit === undefined ? {} : { query: { limit } }),
          ...(signal ? { signal } : {}),
        }),
      // The watermark is handed back exactly as it arrived: it is the server's
      // encoding of per-folder UID state and nothing here may interpret it.
      newMail: (accountId, after, signal) =>
        http.request<NewMailPoll>(`${acct(accountId)}/new-mail`, {
          ...(after === undefined || after === '' ? {} : { query: { after } }),
          ...(signal ? { signal } : {}),
        }),
    },

    preferences: {
      get: (signal) => http.request<UserPreferences>('/preferences', signal ? { signal } : {}),
      update: (prefs) =>
        http.request<UserPreferences>('/preferences', { method: 'PUT', body: prefs }),
      testVideoCall: (url) =>
        http.request<VideoCallTestResult>('/preferences/video-call/test', {
          method: 'POST',
          body: { url },
        }),
    },

    ai: {
      settings: (signal) => http.request<AiSettings>('/ai/settings', signal ? { signal } : {}),
      updateSettings: (input) =>
        http.request<AiSettings>('/ai/settings', { method: 'PUT', body: input }),
      saveKey: (key) => http.request<AiSettings>('/ai/key', { method: 'PUT', body: { key } }),
      removeKey: () => http.request<AiSettings>('/ai/key', { method: 'DELETE' }),
      testKey: (key) =>
        http.request<AiKeyTestResult>('/ai/key/test', {
          method: 'POST',
          body: key === undefined ? {} : { key },
        }),
      models: (options, signal) =>
        http.request<AiModels>('/ai/models', {
          ...(options?.refresh === true ? { query: { refresh: 1 } } : {}),
          ...(signal ? { signal } : {}),
        }),
      assist: (request, onText, signal) => streamAiAnswer('/ai/assist', request, onText, undefined, signal),
      assistMessage: (accountId, messageId, request, onText, onMeta, signal) =>
        streamAiAnswer(
          `${acct(accountId)}/messages/${id(messageId)}/assist`,
          request,
          onText,
          onMeta,
          signal,
        ),
    },

    tasks: {
      lists: (accountId, signal) =>
        http.request<TaskListsResponse>(`${acct(accountId)}/tasks/lists`, signal ? { signal } : {}),
      createList: (accountId, input) =>
        http.request<TaskList>(`${acct(accountId)}/tasks/lists`, { method: 'POST', body: input }),
      updateList: (accountId, listId, patch) =>
        http.request<TaskList>(`${acct(accountId)}/tasks/lists/${id(listId)}`, {
          method: 'PATCH',
          body: patch,
        }),
      deleteList: (accountId, listId) =>
        http.request<void>(`${acct(accountId)}/tasks/lists/${id(listId)}`, { method: 'DELETE' }),
      moveList: (accountId, listId, afterId) =>
        http.request<TaskList>(`${acct(accountId)}/tasks/lists/${id(listId)}/move`, {
          method: 'POST',
          body: { afterId },
        }),
      tasks: async (accountId, listId, signal) =>
        (
          await http.request<{ tasks: Task[] }>(
            `${acct(accountId)}/tasks/lists/${id(listId)}/tasks`,
            signal ? { signal } : {},
          )
        ).tasks,
      smart: async (accountId, view, dueBefore, signal) =>
        (
          await http.request<{ tasks: Task[] }>(`${acct(accountId)}/tasks/items`, {
            query: view === 'due' ? { view, dueBefore } : { view },
            ...(signal ? { signal } : {}),
          })
        ).tasks,
      // Both carry this device's zone: a reminder rings at a wall-clock time,
      // and an owner whose Settings -> General zone is "this device's" has
      // no zone the server could know otherwise (ADR 0073).
      createTask: (accountId, listId, input) =>
        http.request<Task>(`${acct(accountId)}/tasks/lists/${id(listId)}/tasks`, {
          method: 'POST',
          body: { ...input, timeZone: deviceTimeZone() },
        }),
      updateTask: (accountId, taskId, patch) =>
        http.request<Task>(`${acct(accountId)}/tasks/items/${id(taskId)}`, {
          method: 'PATCH',
          body: { ...patch, timeZone: deviceTimeZone() },
        }),
      deleteTask: (accountId, taskId) =>
        http.request<void>(`${acct(accountId)}/tasks/items/${id(taskId)}`, { method: 'DELETE' }),
      completeTask: (accountId, taskId) =>
        http.request<Task>(`${acct(accountId)}/tasks/items/${id(taskId)}/complete`, {
          method: 'POST',
        }),
      uncompleteTask: (accountId, taskId) =>
        http.request<Task>(`${acct(accountId)}/tasks/items/${id(taskId)}/uncomplete`, {
          method: 'POST',
        }),
      moveTask: (accountId, taskId, input) =>
        http.request<Task>(`${acct(accountId)}/tasks/items/${id(taskId)}/move`, {
          method: 'POST',
          body: input,
        }),
      clearCompleted: (accountId, listId) =>
        http.request<{ deleted: number }>(
          `${acct(accountId)}/tasks/lists/${id(listId)}/clear-completed`,
          { method: 'POST' },
        ),
      byEmail: async (accountId, email, signal) =>
        (
          await http.request<{ tasks: Task[] }>(`${acct(accountId)}/tasks/by-email`, {
            query: {
              ...(email.rfcMessageId ? { rfcMessageId: email.rfcMessageId } : {}),
              ...(email.messageId ? { messageId: email.messageId } : {}),
            },
            ...(signal ? { signal } : {}),
          })
        ).tasks,
      resolveEmail: (accountId, taskId, signal) =>
        http.request<TaskEmailResolution>(
          `${acct(accountId)}/tasks/items/${id(taskId)}/email`,
          signal ? { signal } : {},
        ),
    },

    scheduling: {
      status: (accountId, signal) =>
        http.request<SchedulingStatus>(`${acct(accountId)}/scheduling`, signal ? { signal } : {}),
      schedule: (accountId, input) =>
        http.request<ScheduledSend>(`${acct(accountId)}/scheduled`, { method: 'POST', body: input }),
      listScheduled: (accountId, signal) =>
        http.request<ScheduledSendList>(`${acct(accountId)}/scheduled`, signal ? { signal } : {}),
      sendNow: (accountId, jobId, options) =>
        http.request<ScheduledSendNowResult>(`${acct(accountId)}/scheduled/${id(jobId)}/send-now`, {
          method: 'POST',
          // Exactly `{}` unless confirming (DisallowUnknownFields on the server).
          body: options?.confirmUncertain === true ? { confirmUncertain: true } : {},
        }),
      cancelScheduled: (accountId, jobId) =>
        http.request<ScheduledCancelResult>(`${acct(accountId)}/scheduled/${id(jobId)}/cancel`, {
          method: 'POST',
          body: {},
        }),
      snooze: (accountId, input) =>
        http.request<SnoozeResult>(`${acct(accountId)}/snoozes`, {
          method: 'POST',
          body: { messageIds: input.messageIds, wakeAt: input.wakeAt },
        }),
      listSnoozes: (accountId, signal) =>
        http.request<SnoozeList>(`${acct(accountId)}/snoozes`, signal ? { signal } : {}),
      unsnooze: (accountId, jobId) =>
        http.request<UnsnoozeResult>(`${acct(accountId)}/snoozes/${id(jobId)}/unsnooze`, {
          method: 'POST',
          body: {},
        }),
    },

    // Labels (plan/17 §4). The list comes wrapped in `items`, as the
    // contract writes it; the client hands back the array.
    labels: {
      list: async (accountId, signal) =>
        (
          await http.request<{ items: Label[] }>(`${acct(accountId)}/labels`, signal ? { signal } : {})
        ).items,
      create: (accountId, input) =>
        http.request<Label>(`${acct(accountId)}/labels`, { method: 'POST', body: input }),
      update: (accountId, labelId, patch) =>
        http.request<Label>(`${acct(accountId)}/labels/${id(labelId)}`, { method: 'PATCH', body: patch }),
      reorder: async (accountId, labelIds) =>
        (
          await http.request<{ items: Label[] }>(`${acct(accountId)}/labels/order`, {
            method: 'POST',
            body: { labelIds },
          })
        ).items,
      remove: (accountId, labelId) =>
        http.request<Label>(`${acct(accountId)}/labels/${id(labelId)}`, { method: 'DELETE' }),
      migrate: (accountId, labelId) =>
        http.request<LabelMigrateResult>(`${acct(accountId)}/labels/${id(labelId)}/migrate`, {
          method: 'POST',
        }),
      apply: (accountId, input) =>
        http.request<BulkResult>(`${acct(accountId)}/messages/labels`, { method: 'POST', body: input }),
    },

    // Nextcloud (plan 18 §5, ADR 0084). Person-scoped at the root, as `/ai`.
    nextcloud: {
      getNextcloud: (signal) => http.request<NextcloudStatus>('/nextcloud', signal ? { signal } : {}),
      startNextcloudLink: (baseUrl) =>
        http.request<NextcloudLinkStarted>('/nextcloud/link', { method: 'POST', body: { baseUrl } }),
      // Both answers are successes, and only the status tells them apart:
      // 202 is "not granted yet", 200 is the finished link.
      pollNextcloudLink: async (linkId, signal) => {
        const { status, body } = await http.requestWithStatus<NextcloudPollPending | NextcloudStatus>(
          `/nextcloud/link/${id(linkId)}/poll`,
          { method: 'POST', ...(signal ? { signal } : {}) },
        );
        return status === 202
          ? { pending: body as NextcloudPollPending }
          : { linked: body as NextcloudStatus };
      },
      cancelNextcloudLink: (linkId) =>
        http.request<void>(`/nextcloud/link/${id(linkId)}`, { method: 'DELETE' }),
      // Write-only: the answer is the status, which never carries the password.
      putNextcloudCredential: (input) =>
        http.request<NextcloudStatus>('/nextcloud/credential', {
          method: 'PUT',
          body: { baseUrl: input.baseUrl, loginName: input.loginName, appPassword: input.appPassword },
        }),
      verifyNextcloud: () => http.request<NextcloudStatus>('/nextcloud/verify', { method: 'POST' }),
      unlinkNextcloud: () => http.request<NextcloudUnlinked>('/nextcloud', { method: 'DELETE' }),
      putNextcloudSettings: (input) =>
        http.request<NextcloudStatus>('/nextcloud/settings', { method: 'PUT', body: input }),
      listNextcloudFiles: (path, signal) =>
        http.request<NextcloudListing>('/nextcloud/files', {
          query: { path },
          ...(signal ? { signal } : {}),
        }),
      stageNextcloudFile: (path) =>
        http.request<StagedAttachment>('/nextcloud/files/stage', { method: 'POST', body: { path } }),
      shareNextcloudFile: (input) =>
        http.request<NextcloudShared>('/nextcloud/files/share', { method: 'POST', body: input }),
      reserveNextcloudUpload: (input) =>
        http.request<NextcloudUploadReserved>('/nextcloud/uploads', { method: 'POST', body: input }),
      // The raw bytes of the slice, over XHR for the progress events and
      // signed like every other request. The route is outside the
      // Idempotency middleware (it would buffer the body); the key the
      // transport sends anyway is ignored, and a re-PUT of chunk n is
      // idempotent by construction.
      putNextcloudChunk: (uploadId, n, chunk, options) =>
        http.requestUpload<void>(`/nextcloud/uploads/${id(uploadId)}/chunks/${n}`, {
          method: 'PUT',
          body: chunk,
          ...(options?.onProgress ? { onProgress: options.onProgress } : {}),
          ...(options?.signal ? { signal: options.signal } : {}),
        }),
      // The generated password, when asked for, is in this answer and
      // nowhere else: it goes to the caller and is not cached here.
      finishNextcloudUpload: (uploadId, input) =>
        http.request<NextcloudShared>(`/nextcloud/uploads/${id(uploadId)}/finish`, {
          method: 'POST',
          body: input ?? {},
        }),
      deleteNextcloudUpload: async (uploadId) =>
        (
          await http.request<{ upload: NextcloudUpload }>(`/nextcloud/uploads/${id(uploadId)}`, {
            method: 'DELETE',
          })
        ).upload,
      revokeNextcloudUpload: async (uploadId) =>
        (
          await http.request<{ upload: NextcloudUpload }>(`/nextcloud/uploads/${id(uploadId)}/revoke`, {
            method: 'POST',
          })
        ).upload,
      touchNextcloudUploads: async (ids) =>
        (
          await http.request<{ touched: number }>('/nextcloud/uploads/touch', {
            method: 'POST',
            body: { ids },
          })
        ).touched,
      listNextcloudUploads: (query, signal) =>
        http.request<NextcloudUploadPage>('/nextcloud/uploads', {
          query: {
            ...(query?.state !== undefined ? { state: query.state } : {}),
            ...(query?.cursor !== undefined ? { cursor: query.cursor } : {}),
          },
          ...(signal ? { signal } : {}),
        }),
      createNextcloudFolder: (path) =>
        http.request<NextcloudFolderCreated>('/nextcloud/folders', { method: 'POST', body: { path } }),
      cancelNextcloudSave: (saveId) =>
        http.request<void>(`/nextcloud/saves/${id(saveId)}/cancel`, { method: 'POST' }),
      saveAttachments: async (accountId, messageId, input, onEvent, signal) => {
        let done: Extract<NextcloudSaveEvent, { type: 'done' }> | undefined;
        await http.requestEventStream(
          `${acct(accountId)}/messages/${id(messageId)}/save-to-nextcloud`,
          input,
          (event, data, raw) => {
            const payload = (data ?? {}) as Record<string, unknown>;
            switch (event) {
              case 'start':
              case 'progress':
              case 'moved':
              case 'saved':
                onEvent({ ...payload, type: event } as NextcloudSaveEvent);
                return;
              case 'failed': {
                // Rebuilt from the wire as the server wrote it: an error's
                // details are read by their snake_case names everywhere.
                const wire = (raw ?? {}) as Record<string, unknown>;
                const details = wire.details;
                onEvent({
                  type: 'failed',
                  partId: String(wire.part_id ?? ''),
                  status: typeof wire.status === 'number' ? wire.status : 502,
                  error: {
                    code: typeof wire.code === 'string' ? wire.code : ApiErrorCodes.Internal,
                    message: typeof wire.message === 'string' ? wire.message : '',
                    ...(details !== null && typeof details === 'object'
                      ? { details: details as Record<string, unknown> }
                      : {}),
                  },
                });
                return;
              }
              case 'done':
                done = { ...payload, type: 'done' } as Extract<NextcloudSaveEvent, { type: 'done' }>;
                onEvent(done);
                return;
              default:
              // Anything newer than this client is ignored.
            }
          },
          signal,
        );
        // A stream that ended without `done` was cut off on the way (a proxy,
        // a dropped connection): what was saved was reported; the rest is not
        // known.
        if (done === undefined) {
          throw new ApiError(502, {
            error: {
              code: ApiErrorCodes.UpstreamFailure,
              message: 'The connection was cut off before the save finished. Check Nextcloud for what arrived.',
            },
          });
        }
        return done;
      },
    },

    // Copies of sent mail not in Sent yet (#84). Every POST carries exactly
    // `{}`: the server decodes with DisallowUnknownFields and requires a body.
    sentCopies: {
      list: async (accountId, signal) =>
        (
          await http.request<{ items: SentCopy[] }>(
            `${acct(accountId)}/sent-copies`,
            signal ? { signal } : {},
          )
        ).items,
      run: (accountId) =>
        http.request<SentCopyRunResult>(`${acct(accountId)}/sent-copies/run`, {
          method: 'POST',
          body: {},
        }),
      save: (accountId, copyId) =>
        http.request<SavedSentCopy>(`${acct(accountId)}/sent-copies/${id(copyId)}/save`, {
          method: 'POST',
          body: {},
        }),
      // Through the transport, not a navigation: the session is proof-bound
      // (ADR 0041), so a plain `<a href>` would carry no proof header.
      eml: (accountId, copyId) =>
        http.requestBlob(`${acct(accountId)}/sent-copies/${id(copyId)}/eml`),
      dismiss: (accountId, copyId) =>
        http.request<void>(`${acct(accountId)}/sent-copies/${id(copyId)}`, { method: 'DELETE' }),
      settings: (accountId, signal) =>
        http.request<SentSettings>(`${acct(accountId)}/sent-settings`, signal ? { signal } : {}),
      updateSettings: (accountId, saveSentCopy) =>
        http.request<SentSettings>(`${acct(accountId)}/sent-settings`, {
          method: 'PUT',
          body: { saveSentCopy },
        }),
    },
  };
}

/** This device's IANA time zone, or undefined when the browser will not say. */
function deviceTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}
